import type { Project } from '@/pages/Insights/data/projectsRepo';

// Closing tags of every context block are stripped from user-authored text, so a project's
// instructions cannot end a block early and pose as the system's own context.
const escapeForBlock = (s: string): string =>
  s.replace(/<\/(project|screen|organization)_context>/gi, '');

/**
 * Serialize an AI Insights project into a `<project_context>` block, appended to the request's
 * organizationContext like `<screen_context>` (see screenContext.ts). The project's instructions
 * (shown as "Description" in its settings) apply to every conversation in it, and its knowledge is
 * background the answers can rely on. Returns undefined when the project adds nothing.
 */
export const renderProjectContextBlock = (project: Project): string | undefined => {
  const instructions = [project.instructions, project.description]
    .map((s) => s?.trim())
    .filter(Boolean)
    .join('\n\n');
  const knowledge = project.knowledge?.trim();
  if (!instructions && !knowledge) return undefined;

  const lines: string[] = [
    '<project_context>',
    `This conversation belongs to the project "${escapeForBlock(project.name)}".`,
  ];
  if (instructions) {
    lines.push('Follow these project instructions in every answer:');
    lines.push(escapeForBlock(instructions));
  }
  if (knowledge) {
    lines.push('Project knowledge to rely on:');
    lines.push(escapeForBlock(knowledge));
  }
  lines.push('</project_context>');
  return lines.join('\n');
};
